import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('rabathub_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('rabathub_token');
      localStorage.removeItem('rabathub_user');
      window.dispatchEvent(new Event('auth-logout'));
    }
    return Promise.reject(error);
  }
);

// Generate or get session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('rabathub_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('rabathub_session_id', sessionId);
  }
  return sessionId;
};

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('rabathub_token', access_token);
    localStorage.setItem('rabathub_user', JSON.stringify(user));
    return response.data;
  },

  register: async (email, password, name) => {
    const response = await apiClient.post('/auth/register', { email, password, name });
    const { access_token, user } = response.data;
    localStorage.setItem('rabathub_token', access_token);
    localStorage.setItem('rabathub_user', JSON.stringify(user));
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('rabathub_token');
    localStorage.removeItem('rabathub_user');
  },

  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('rabathub_user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('rabathub_token');
  },

  isAdmin: () => {
    const user = authAPI.getCurrentUser();
    return user?.role === 'admin';
  },

  addFavorite: async (shopId) => {
    const response = await apiClient.post(`/auth/favorites/${shopId}`);
    return response.data;
  },

  removeFavorite: async (shopId) => {
    const response = await apiClient.delete(`/auth/favorites/${shopId}`);
    return response.data;
  },

  getFavorites: async () => {
    const response = await apiClient.get('/auth/favorites');
    return response.data;
  },
};

// Shop API
export const shopAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/shops', { params });
    return response.data;
  },
  
  getById: async (shopId) => {
    const response = await apiClient.get(`/shops/${shopId}`);
    return response.data;
  },
};

// Categories API
export const categoriesAPI = {
  getAll: async () => {
    const response = await apiClient.get('/categories');
    return response.data;
  },
};

// Tracking API
export const trackingAPI = {
  trackClick: async (shopId, discountCodeId = null, clickType = 'shop_visit') => {
    const response = await apiClient.post('/track/click', {
      shop_id: shopId,
      discount_code_id: discountCodeId,
      session_id: getSessionId(),
      click_type: clickType,
      device_type: 'web',
      referrer: window.location.href,
    });
    return response.data;
  },
  
  trackCodeCopy: async (discountCodeId) => {
    const response = await apiClient.post('/track/code-copy', null, {
      params: {
        discount_code_id: discountCodeId,
        session_id: getSessionId(),
      },
    });
    return response.data;
  },
};

// Admin API (for analytics dashboard)
export const adminAPI = {
  getAnalytics: async (startDate, endDate) => {
    const response = await apiClient.get('/admin/analytics', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
  
  getStatsSummary: async () => {
    const response = await apiClient.get('/admin/stats/summary');
    return response.data;
  },
};

export default {
  auth: authAPI,
  shop: shopAPI,
  categories: categoriesAPI,
  tracking: trackingAPI,
  admin: adminAPI,
};
